import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface RotinaAnaliticaReal {
  id: number;
  nome?: string;
  tipo?: string;
  objetivo?: string;
  procedimento?: string;
  ativo?: boolean;
}

export interface ExecucaoAnaliticaReal {
  id?: number;
  codigo?: string | null;
  pericia_id?: number;
  rotina_id: number | null;
  rotina_nome?: string;
  rotina_tipo?: string;
  status: string;
  parametros_json?: string | null;
  resumo_json?: string | null;
  texto_tecnico?: string | null;
  total_registros?: number | null;
  total_achados?: number | null;
  arquivo_entrada_principal?: string | null;
  arquivo_saida_resumo?: string | null;
  arquivo_saida_achados?: string | null;
  log_execucao?: string | null;
  created_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface AchadoAnaliticoReal {
  id?: number;
  codigo?: string | null;
  execucao_id: number | null;
  pericia_id?: number;
  tipo_achado: string;
  gravidade: string;
  descricao: string;
  evidencia?: string | null;
  valor_relacionado?: string | null;
  linha_origem?: string | null;
  recomendacao?: string | null;
  confirmado?: boolean | null;
  revisado?: boolean | null;
}

export interface MatrizEvidenciaReal {
  id?: number;
  codigo?: string | null;
  pericia_id?: number;
  quesito_id?: number | null;
  documento_id?: number | null;
  papel_trabalho_id?: number | null;
  procedimento?: string | null;
  achado?: string | null;
  conclusao?: string | null;
  limitacao?: string | null;
  created_at?: string;
}

export interface LaudoChecklistReal {
  id?: number;
  codigo?: string | null;
  pericia_id?: number;
  laudo_concluido?: boolean | null;
  comunicacao_assistente?: boolean | null;
  diligencia_conjunta?: boolean | null;
  checklist_objeto_ok?: boolean | null;
  checklist_quesitos_ok?: boolean | null;
  checklist_documentos_ok?: boolean | null;
  checklist_evidencias_ok?: boolean | null;
  checklist_calculos_ok?: boolean | null;
  checklist_limitacoes_ok?: boolean | null;
  observacoes?: string | null;
  status?: string | null;
  created_at?: string;
}

@Injectable({ providedIn: 'root' })
export class AnaliticoRealService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api';

  listarRotinas(periciaId: number): Observable<RotinaAnaliticaReal[]> {
    return this.http.get<RotinaAnaliticaReal[]>(`${this.apiUrl}/pericias/${periciaId}/rotinas-analiticas`);
  }

  listarExecucoes(periciaId: number): Observable<ExecucaoAnaliticaReal[]> {
    return this.http.get<ExecucaoAnaliticaReal[]>(`${this.apiUrl}/pericias/${periciaId}/execucoes-analiticas`);
  }

  criarExecucao(periciaId: number, payload: ExecucaoAnaliticaReal): Observable<ExecucaoAnaliticaReal> {
    return this.http.post<ExecucaoAnaliticaReal>(`${this.apiUrl}/pericias/${periciaId}/execucoes-analiticas`, payload);
  }

  excluirExecucao(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/execucoes-analiticas/${id}`);
  }

  listarAchados(periciaId: number): Observable<AchadoAnaliticoReal[]> {
    return this.http.get<AchadoAnaliticoReal[]>(`${this.apiUrl}/pericias/${periciaId}/achados-analiticos`);
  }

  criarAchado(periciaId: number, payload: AchadoAnaliticoReal): Observable<AchadoAnaliticoReal> {
    return this.http.post<AchadoAnaliticoReal>(`${this.apiUrl}/pericias/${periciaId}/achados-analiticos`, payload);
  }

  excluirAchado(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/achados-analiticos/${id}`);
  }

  listarEvidencias(periciaId: number): Observable<MatrizEvidenciaReal[]> {
    return this.http.get<MatrizEvidenciaReal[]>(`${this.apiUrl}/pericias/${periciaId}/matriz-evidencias-real`);
  }

  criarEvidencia(periciaId: number, payload: MatrizEvidenciaReal): Observable<MatrizEvidenciaReal> {
    return this.http.post<MatrizEvidenciaReal>(`${this.apiUrl}/pericias/${periciaId}/matriz-evidencias-real`, payload);
  }

  excluirEvidencia(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/matriz-evidencias-real/${id}`);
  }

  listarLaudosChecklist(periciaId: number): Observable<LaudoChecklistReal[]> {
    return this.http.get<LaudoChecklistReal[]>(`${this.apiUrl}/pericias/${periciaId}/laudos-checklist-real`);
  }

  criarLaudoChecklist(periciaId: number, payload: LaudoChecklistReal): Observable<LaudoChecklistReal> {
    return this.http.post<LaudoChecklistReal>(`${this.apiUrl}/pericias/${periciaId}/laudos-checklist-real`, payload);
  }

  atualizarLaudoChecklist(id: number, payload: LaudoChecklistReal): Observable<LaudoChecklistReal> {
    return this.http.put<LaudoChecklistReal>(`${this.apiUrl}/laudos-checklist-real/${id}`, payload);
  }

  excluirLaudoChecklist(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/laudos-checklist-real/${id}`);
  }
}
